import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons/faXmark';
import { faArrowUpRightFromSquare } from '@fortawesome/pro-regular-svg-icons/faArrowUpRightFromSquare';
import { FormattedMessage, useIntl } from 'react-intl';
import type { MessageDescriptor } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { FlexRow } from 'ui-components/components/flex/row';
import { Pill } from 'ui-components/components/pill';
import { Text, TextType } from 'ui-components/components/text';
import { THEME_CONSTANTS } from 'ui-components/services/button-theme-service';

import style from './recommendation-popover-card.module.less';
import popoverOverrides from './popover-overrides.module.less';
import { messages } from './messages';

type Props = {
  title: MessageDescriptor;
  description: MessageDescriptor;
  descriptionValues?: Record<string, string | number>;
  primaryButtonLabel: MessageDescriptor;
  onPrimaryAction: () => void;
  onClose: () => void;
  onLearnMoreClick?: () => void;
  learnMoreUrl?: string;
  illustrationSrc: string;
};

// Export classNames for Mantine Popover
// - dropdown: structural overrides to remove Mantine defaults
// - arrow: content-specific styling that matches the card border
export const popoverClassNames = {
  dropdown: popoverOverrides.dropdown,
  arrow: style['popover-arrow'],
};

export const RecommendationPopoverCard = ({
  title,
  description,
  descriptionValues,
  primaryButtonLabel,
  onPrimaryAction,
  onClose,
  onLearnMoreClick,
  learnMoreUrl,
  illustrationSrc,
}: Props) => {
  const intl = useIntl();

  return (
    <FlexColumn className={style.card}>
      <Button
        ariaLabel={intl.formatMessage(messages.close)}
        className={style['close-button']}
        onClick={onClose}
        theme={THEME_CONSTANTS.unstyled}
      >
        <FontAwesomeIcon icon={faXmark} />
      </Button>

      <FlexColumn className={style['image-area']}>
        <img alt='' className={style.illustration} src={illustrationSrc} />
      </FlexColumn>

      <FlexColumn className={style.body} gap={Gap.Space16}>
        <FlexColumn gap={Gap.Space12}>
          <Pill className={style['recommended-badge']} label={<FormattedMessage {...messages.recommendedBadge} />} />
          <FlexColumn gap={Gap.Space8}>
            <Text textType={TextType.Label} value={title} />
            <Text formattedMessageValues={descriptionValues} textType={TextType.Paragraph} value={description} />
          </FlexColumn>
        </FlexColumn>

        <FlexRow className={style['card-actions']} gap={Gap.Space8}>
          <Button onClick={onPrimaryAction} theme={THEME_CONSTANTS.white}>
            <FormattedMessage {...primaryButtonLabel} />
          </Button>
          {learnMoreUrl && (
            <Button href={learnMoreUrl} onClick={onLearnMoreClick} rel='noopener noreferrer' target='_blank' theme={THEME_CONSTANTS.link}>
              <FormattedMessage {...messages.learnMore} />
              <FontAwesomeIcon className={style['learn-more-icon']} icon={faArrowUpRightFromSquare} />
            </Button>
          )}
        </FlexRow>
      </FlexColumn>
    </FlexColumn>
  );
};
